import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getPagePathnameForTabValue,
  PAGE_TAB_VALUES,
  type PageTabValue,
} from '@constants/pageTabs';
import { ROUTES } from '@constants/routes';
import {
  isSupportedMarketCode,
  type SupportedMarketCode,
} from '@constants/markets';
import { persistLastInstrumentContext } from '@utils/iframeContextStorage';
import { validateIframeParams } from '@utils/iframeParamValidation';
import { isPageTabAvailableForMarket } from '@utils/pageTabAvailability';
import { appendPageTabParam } from '@utils/queryParams';
import { notify } from '@utils/notify';

const getResolvedPathname = (pathname: string, tab: PageTabValue | undefined) => {
  if (pathname === ROUTES.login) return pathname;
  if (!tab) return pathname;
  return getPagePathnameForTabValue(tab);
};

/**
 * Every iframe route must receive ticker/mkt/token/tab from the parent URL.
 * Missing, blank, `null`, or unexpected params block the widget before any
 * API-backed page mounts.
 *
 * A second, silent access rule also applies: some market/page combinations are
 * unavailable (e.g. US Instructions `tab=5`). When a valid URL requests an
 * unavailable tab, the URL is replaced (not pushed) with an allowed tab so the
 * blocked page and its queries never mount. This restriction is deliberately
 * silent: it never calls `notify()`. Genuine parameter validation messaging is
 * unchanged.
 */
export const useEnsureIframeParams = (): boolean => {
  const location = useLocation();
  const navigate = useNavigate();

  // In-memory only: the last allowed tab per canonical market for the current
  // mounted session. It intentionally resets on remount (hard load, refresh, or
  // a new iframe load) so a parent-driven load is never confused with stale
  // browser state. No localStorage/sessionStorage is used.
  const lastAllowedTabByMarketRef = useRef<
    Partial<Record<SupportedMarketCode, PageTabValue>>
  >({});

  const validation = useMemo(
    () => validateIframeParams(location.search),
    [location.search],
  );

  const mkt = validation.query.mkt;
  const isTabAvailable =
    validation.tab === undefined ||
    isPageTabAvailableForMarket(validation.tab, mkt);

  // For a blocked tab, prefer the last allowed tab recorded for this market in
  // the current session (only if still available), otherwise fall back to
  // Summary (`1`).
  const storedTab = isSupportedMarketCode(mkt)
    ? lastAllowedTabByMarketRef.current[mkt]
    : undefined;
  const fallbackTab: PageTabValue =
    storedTab && isPageTabAvailableForMarket(storedTab, mkt)
      ? storedTab
      : PAGE_TAB_VALUES.summary;

  const resolvedPathname = useMemo(
    () => getResolvedPathname(location.pathname, validation.tab),
    [location.pathname, validation.tab],
  );
  const isBlockedTab =
    validation.isValid && validation.tab !== undefined && !isTabAvailable;
  const replacementSearch = isBlockedTab
    ? appendPageTabParam(location.search, fallbackTab)
    : location.search;

  const issueKey = validation.issues.map((issue) => issue.reason).join('|');
  const primaryIssue = validation.issues[0];
  const hasPagePathname = location.pathname === resolvedPathname;
  const needsRedirect =
    validation.isValid &&
    (resolvedPathname !== location.pathname || isBlockedTab);

  useEffect(() => {
    if (location.pathname === ROUTES.login || !primaryIssue) {
      return;
    }

    // Invalid iframe input blocks the page/API request. Only surface the first
    // issue so callers receive one actionable correction at a time:
    // token -> ticker -> mkt -> tab. The market/page availability rule is NOT a
    // validation issue and is never notified here.
    notify({ level: 'error', message: primaryIssue.message });
  }, [issueKey, location.pathname, primaryIssue]);

  // Record the last allowed tab for the current canonical market so an
  // in-session attempt to reach a blocked tab can return to it.
  useEffect(() => {
    if (!validation.isValid || !validation.tab || !isTabAvailable) return;
    if (!isSupportedMarketCode(mkt)) return;
    lastAllowedTabByMarketRef.current[mkt] = validation.tab;
  }, [validation.isValid, validation.tab, isTabAvailable, mkt]);

  useEffect(() => {
    if (location.pathname === ROUTES.login || !validation.isValid) return;

    persistLastInstrumentContext({
      ticker: validation.query.ticker as string,
      mkt: validation.query.mkt as string,
    });

    if (!needsRedirect) {
      return;
    }

    // Only `tab` (and, when needed, the canonical pathname) changes. Every other
    // query param, the hash, and compatible router state are preserved. Replace
    // semantics stop Back from looping through the blocked location.
    navigate(
      {
        pathname: resolvedPathname,
        search: replacementSearch,
        hash: location.hash,
      },
      {
        replace: true,
        state: location.state,
      },
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
    needsRedirect,
    replacementSearch,
    resolvedPathname,
    validation.isValid,
    validation.query.mkt,
    validation.query.ticker,
  ]);

  if (location.pathname === ROUTES.login) return true;

  // A blocked tab returns false until the replacement navigation is active, so
  // `AnalysisRoute` renders no protected page and the height/analytics hook
  // emits nothing for the blocked tab.
  return validation.isValid && hasPagePathname && !isBlockedTab;
};
